// app/plugins/fetch.client.ts - 全局 $fetch 客户端拦截器
// TODO(Phase4+): 错误消息改为 i18n 文案映射
// TODO(Enhance): 登录过期优先尝试 refresh/change_token 成功后重试原请求
// TODO(Enhance): 网络/超时场景加入有限次重试与退避策略
// TODO(Enhance): 支持 AbortController 以便组件卸载时取消请求
// TODO(Enhance): 请求去重/并发上限控制、缓存（GET）策略
// TODO(Enhance): 调试日志开关通过环境变量控制，生产默认关闭

import { ElMessage } from 'element-plus'
import { buildSignatureHeaders } from '~/utils/signature'
import { useApiConfig } from '~/composables/useApiConfig'
import { useUserStore } from '~/stores/user'
import type { ApiResponse } from '~/types/api'
import { ApiError, ApiErrorType } from '~/types/api'
import { ERROR_CODES, ERROR_MESSAGES, HTTP_STATUS_MESSAGES } from '~/utils/constants'

export default defineNuxtPlugin(() => {
  const { getConfig, getCommonRequestFields } = useApiConfig()

  // 单例刷新控制，避免并发抖动
  let refreshPromise: Promise<boolean> | null = null

  async function refreshTokenOnce(fetcher: typeof $fetch): Promise<boolean> {
    if (refreshPromise) return refreshPromise
    const userStore = useUserStore()
    const rt = userStore.loginData?.refreshToken
    if (!rt) {
      refreshPromise = Promise.resolve(false)
      return refreshPromise
    }
    refreshPromise = (async () => {
      try {
        const resp: any = await (fetcher as any)('/account/change_token', { method: 'POST', body: { refresh_token: rt } })
        const d = (resp && resp.data) ? resp.data : resp
        if (!d || !d.token) return false
        userStore.updateTokens(d.token, d.refresh_token)
        if (import.meta.dev) {
          // eslint-disable-next-line no-console
          console.debug('[Auth] token refreshed')
        }
        return true
      } catch (e) {
        if (import.meta.dev) {
          // eslint-disable-next-line no-console
          console.debug('[Auth] token refresh failed', e)
        }
        return false
      } finally {
        // 允许下一次刷新
        setTimeout(() => { refreshPromise = null }, 0)
      }
    })()
    return refreshPromise
  }

  // 原始 fetch 实例：仅负责注入 common、签名与把业务错误转为异常（不做刷新重试）
  const rawFetch = $fetch.create({
    // 动态获取 baseURL 和 timeout（避免在 plugin 顶层调用 getConfig）
    get baseURL() {
      return getConfig().baseURL
    },
    get timeout() {
      return getConfig().timeout
    },

    async onRequest({ request, options }) {
      const userStore = useUserStore()
      const cfg = getConfig() // 在钩子内调用，此时 useI18n 已准备好

      const common = {
        ...getCommonRequestFields(),
        uid: userStore.loginData?.uid || '',
        token: userStore.loginData?.token || ''
      }

      const originalBody = (options.body as any) || {}
      const mergedBody = { common, ...originalBody }
      options.body = mergedBody

      const method = (options.method || 'POST').toString().toUpperCase()
      let path = '/'
      try {
        if (typeof request === 'string') {
          path = request.startsWith('http') ? new URL(request).pathname : request
        }
      } catch (e) {
        path = typeof request === 'string' ? request : '/'
        if (import.meta.dev) {
          // eslint-disable-next-line no-console
          console.debug('[API Request] parse path failed, fallback to raw string', e)
        }
      }

      const bodyString = JSON.stringify(mergedBody)
      const sigHeaders = buildSignatureHeaders({
        method,
        bodyString,
        path,
        accessKey: cfg.accessKey,
        accessSecret: cfg.accessSecret
      })

      options.headers = {
        ...(options.headers || {}),
        ...sigHeaders
      }

      if (import.meta.dev) {
        // eslint-disable-next-line no-console
        console.debug('[API Request]', method, path, mergedBody)
      }
    },

    async onResponse({ response, options }): Promise<void> {
      const body = response._data as ApiResponse
      if (!body || !body.resp_common) {
        // eslint-disable-next-line no-console
        console.warn('[API Warning] 响应缺少 resp_common 字段', response)
        return
      }

      const { ret, msg, request_id } = body.resp_common
      if (ret === 0) {
        if (import.meta.dev) {
          // eslint-disable-next-line no-console
          console.debug('[API Success]', options.method, response.url, { request_id })
        }
        return
      }

      if (ret === ERROR_CODES.LOGIN_EXPIRED) {
        // 不在此处刷新；交由包装的 apiFetch 处理刷新与一次性重试
        {
          const err = new ApiError(ApiErrorType.LOGIN_EXPIRED, msg || '登录已过期，请重新登录', ret)
          if (typeof request_id === 'string') err.requestId = request_id
          throw err
        }
      }

      // 5513002: invalid token -> 直接要求重新登录（不尝试刷新）
      if (ret === ERROR_CODES.INVALID_TOKEN) {
        const userStore = useUserStore()
        userStore.clearLoginState()
        userStore.setAuthModalVisible(true)
        const warnMsg = ERROR_MESSAGES[ERROR_CODES.INVALID_TOKEN] || '登录状态无效，请重新登录'
        ElMessage.warning(warnMsg)
        {
          const err = new ApiError(ApiErrorType.LOGIN_EXPIRED, msg || warnMsg, ret)
          if (request_id) err.requestId = request_id
          throw err
        }
      }

      const bizMsg = msg || ERROR_MESSAGES[ret] || '操作失败，请稍后重试'
      ElMessage.error(bizMsg)
      if (import.meta.dev) {
        // eslint-disable-next-line no-console
        console.debug('[API BusinessError]', options.method, response.url, { ret, request_id, msg })
      }
      {
        const err = new ApiError(ApiErrorType.BUSINESS, bizMsg, ret)
        if (typeof request_id === 'string') err.requestId = request_id
        throw err
      }
    },

    async onResponseError({ response, error }) {
      if (response) {
        const status = response.status
        const message = HTTP_STATUS_MESSAGES[status] || `HTTP 错误 ${status}`
        ElMessage.error(message)
        // TODO(Enhance): 对 429/503/504 等可恢复状态码尝试重试
        if (import.meta.dev) {
          // eslint-disable-next-line no-console
          console.debug('[API HttpError]', response.url, { status })
        }
        throw new ApiError(ApiErrorType.HTTP, message, status)
      }

      const message = '网络连接失败，请检查网络设置'
      ElMessage.error(message)
      // TODO(Enhance): 网络错误退避重试（最多 N 次），并暴露钩子给业务层
      if (import.meta.dev) {
        // eslint-disable-next-line no-console
        console.debug('[API NetworkError]', { error })
      }
      {
        const err = new ApiError(ApiErrorType.NETWORK, message)
        ;(err as any).originalError = error
        throw err
      }
    }
  })

  // 包装一层：捕获登录过期后尝试刷新并透明重试一次
  async function apiFetch<T = any>(path: string, opts?: { method?: string; body?: any; headers?: Record<string, string> }): Promise<ApiResponse<T>> {
    try {
      return await (rawFetch as any)(path, opts) as Promise<ApiResponse<T>>
    } catch (e: any) {
      if (!(e instanceof ApiError) || !e.isLoginExpired()) throw e
      const retried = opts?.headers && (opts.headers as any)['x-cds-retry-once'] === '1'
      if (retried) throw e
      const ok = await refreshTokenOnce(rawFetch as any)
      if (!ok) {
        const userStore = useUserStore()
        userStore.clearLoginState()
        userStore.setAuthModalVisible(true)
        ElMessage.warning('登录已过期，请重新登录')
        throw e
      }
      // 去掉 common，让 onRequest 重新注入 token；标记只重试一次
      const oldBody = (opts?.body as any) || {}
      const retryBody = { ...oldBody }
      if (retryBody.common) delete retryBody.common
      const retryHeaders = { ...(opts?.headers || {}), 'x-cds-retry-once': '1' }
      return await (rawFetch as any)(path, { ...(opts || {}), body: retryBody, headers: retryHeaders }) as Promise<ApiResponse<T>>
    }
  }

  return {
    provide: {
      api: apiFetch
    }
  }
})
